/**
 * SHARED CREDIT ANALYSIS REPORT PAGE
 * 
 * Public page to view shared credit analysis results.
 * No PIN required - accessible via shareable link.
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Target,
  TrendingUp,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  FileText,
  Calculator,
  Scale,
  User,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score color helpers
const getScoreColor = (score) => {
  if (score >= 800) return "text-green-400";
  if (score >= 740) return "text-emerald-400";
  if (score >= 670) return "text-blue-400";
  if (score >= 580) return "text-amber-400";
  return "text-red-400";
};

const getScoreBgColor = (score) => {
  if (score >= 800) return "bg-green-500/20 border-green-500/30";
  if (score >= 740) return "bg-emerald-500/20 border-emerald-500/30";
  if (score >= 670) return "bg-blue-500/20 border-blue-500/30";
  if (score >= 580) return "bg-amber-500/20 border-amber-500/30";
  return "bg-red-500/20 border-red-500/30";
};

const getTierName = (score) => {
  if (score >= 800) return "Exceptional";
  if (score >= 740) return "Very Good";
  if (score >= 670) return "Good";
  if (score >= 580) return "Fair";
  return "Poor";
};

const getScoreGradient = (score) => {
  if (score >= 800) return "from-green-500 to-emerald-500";
  if (score >= 740) return "from-emerald-500 to-teal-500";
  if (score >= 670) return "from-blue-500 to-cyan-500";
  if (score >= 580) return "from-amber-500 to-yellow-500";
  return "from-red-500 to-orange-500";
};

export default function SharedReportPage() {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`${API}/credit-analysis/shared/${shareId}`);
        setReportData(response.data);
      } catch (err) {
        console.error("Error fetching shared report:", err);
        if (err.response?.status === 404) {
          setError("This report was not found or has been deleted.");
        } else if (err.response?.status === 410) {
          setError("This report has expired and is no longer available.");
        } else {
          setError("Unable to load the report. Please check the link and try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchReport();
    }
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading your credit analysis report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Report Unavailable</h2>
            <p className="text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client_name, analysis, projected_scores, simulation_details, created_at } = reportData;

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full mb-4">
            <FileText className="w-5 h-5" />
            <span className="font-semibold">Credit Analysis Report</span>
          </div>
          
          {client_name && (
            <div className="flex items-center justify-center gap-2 text-zinc-400 mt-4">
              <User className="w-4 h-4" />
              <span>Prepared for <span className="text-white font-medium">{client_name}</span></span>
            </div>
          )}
          
          {created_at && (
            <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm mt-2">
              <Clock className="w-3 h-3" />
              <span>Generated {new Date(created_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Score Projection */}
        {projected_scores && (
          <Card className={`border-2 ${getScoreBgColor(projected_scores.projected)}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Your Score Projection
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Score Display */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Current Score */}
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <p className="text-zinc-400 text-sm mb-1">Current Score</p>
                  <p className={`text-4xl font-bold ${getScoreColor(projected_scores.current)}`}>
                    {projected_scores.current}
                  </p>
                  <Badge variant="outline" className={`mt-2 ${getScoreColor(projected_scores.current)}`}>
                    {getTierName(projected_scores.current)}
                  </Badge>
                </div>
                
                {/* Arrow */}
                <div className="flex items-center justify-center">
                  <div className="bg-gradient-to-r from-blue-500 to-green-500 rounded-full p-3">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                {/* Projected Score */}
                <div className={`rounded-lg p-4 text-center bg-gradient-to-br ${getScoreGradient(projected_scores.projected)} bg-opacity-20`}>
                  <p className="text-zinc-300 text-sm mb-1">Projected Score</p>
                  <p className="text-5xl font-bold text-white">
                    {projected_scores.projectedLow || projected_scores.projected}-{projected_scores.projectedHigh || projected_scores.projected + 20}
                  </p>
                  <Badge className="mt-2 bg-white/20 text-white">
                    +{projected_scores.gain} points potential
                  </Badge>
                </div>
              </div>

              {/* Improvement Factors */}
              {simulation_details?.factors?.length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-green-400" />
                    How We Get There
                  </h4>
                  <div className="space-y-2">
                    {simulation_details.factors.map((factor, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                        <div>
                          <p className="text-white text-sm">{factor.factor}</p>
                          <p className="text-zinc-500 text-xs">{factor.description}</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          +{factor.gain} pts
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FICO Factors */}
              {simulation_details?.details && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-blue-400" />
                    FICO Score Factors
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(simulation_details.details).map(([key, data]) => {
                      const labels = {
                        paymentHistory: "Payment History",
                        amountsOwed: "Credit Utilization",
                        creditHistory: "Credit Age",
                        newCredit: "New Credit",
                        creditMix: "Credit Mix"
                      };
                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-400">{labels[key]} ({data.weight}%)</span>
                            <span className="text-white">{data.current}% → {data.potential}%</span>
                          </div>
                          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all"
                              style={{ width: `${data.potential}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Account Summary */}
        {analysis && (
          <Card className="bg-zinc-800/50 border-zinc-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Account Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-zinc-900/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">
                    {(analysis.positive_accounts?.length || 0) + (analysis.negative_accounts?.length || 0)}
                  </p>
                  <p className="text-xs text-zinc-400">Total Accounts</p>
                </div>
                <div className="bg-green-500/10 p-3 rounded-lg text-center border border-green-500/30">
                  <p className="text-2xl font-bold text-green-400">{analysis.positive_accounts?.length || 0}</p>
                  <p className="text-xs text-zinc-400">Positive</p>
                </div>
                <div className="bg-red-500/10 p-3 rounded-lg text-center border border-red-500/30">
                  <p className="text-2xl font-bold text-red-400">{analysis.negative_accounts?.length || 0}</p>
                  <p className="text-xs text-zinc-400">Negative</p>
                </div>
                <div className="bg-blue-500/10 p-3 rounded-lg text-center border border-blue-500/30">
                  <p className="text-2xl font-bold text-blue-400">
                    {simulation_details?.utilization?.current?.toFixed(0) || 0}%
                  </p>
                  <p className="text-xs text-zinc-400">Utilization</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Negative Accounts */}
        {analysis?.negative_accounts?.length > 0 && (
          <Card className="bg-zinc-800/50 border-zinc-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                Items Affecting Your Score ({analysis.negative_accounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analysis.negative_accounts.map((acc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex-1">
                      <p className="text-white font-medium">{acc.creditor || acc.name || 'Unknown'}</p>
                      <p className="text-xs text-zinc-400">
                        {acc.account_type || acc.type || 'Account'} • {acc.status || 'Derogatory'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-400 font-bold">{acc.balance || '$0'}</p>
                      <Badge variant="outline" className="text-red-400 border-red-400/30 text-xs">
                        Action Needed
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Positive Accounts */}
        {analysis?.positive_accounts?.length > 0 && (
          <Card className="bg-zinc-800/50 border-zinc-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Accounts in Good Standing ({analysis.positive_accounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analysis.positive_accounts.map((acc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <div className="flex-1">
                      <p className="text-white font-medium">{acc.creditor || acc.name || 'Unknown'}</p>
                      <p className="text-xs text-zinc-400">
                        {acc.account_type || acc.type || 'Account'} • {acc.status || 'Current'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{acc.balance || '$0'}</p>
                      {acc.credit_limit && (
                        <p className="text-xs text-zinc-500">Limit: {acc.credit_limit}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <p className="text-amber-400 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Score projections are estimates based on FICO scoring factors. Actual results may vary 
              based on your complete credit history, account ages, and other factors.
            </span>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center text-zinc-500 text-sm py-6">
          <p>Powered by Clear All Wizard Credit Repair</p>
        </div>
      </div>
    </div>
  );
}
